import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Settings, Palette, Key, Save, Users, Plus,
  User, Shield, Pencil, Trash2, Lock, Building2, MapPin,
  Upload, X, Image, Rocket, CheckCircle2, Mail, Send
} from "lucide-react";
import { toast } from "sonner";
import { Slider } from "../components/ui/slider";

import { API_URL } from "../lib/utils";

// AdminPage uses its own API_URL for compatibility

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_URL}${url}`;
};

const ROLE_LABELS = {
  admin: { label: "Amministratore", color: "bg-red-500" },
  manager: { label: "Manager", color: "bg-blue-500" },
  operatore: { label: "Operatore", color: "bg-gray-500" }
};

function EmailSettingsTab() {
  const [settings, setSettings] = React.useState({
    smtp_host: "", smtp_port: "587", smtp_user: "", smtp_password: "",
    smtp_security: "starttls", sender_email: "paghe@novahost.it", sender_name: "Ufficio Paghe",
    payslip_subject: "Busta paga {mese}",
    payslip_body: "Gentile {nome},\n\nin allegato la busta paga di {mese}.\n\nCordiali saluti,\nUfficio Paghe"
  });
  const [saving, setSaving] = React.useState(false);
  const [testEmail, setTestEmail] = React.useState("");
  const [testing, setTesting] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API_URL}/api/email-settings`, { withCredentials: true }).then(res => {
      setSettings(prev => ({ ...prev, ...res.data, smtp_password: "" }));
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/email-settings`, settings, { withCredentials: true });
      alert("Impostazioni email salvate");
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    if (!testEmail) return;
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/api/email-settings/test`, { test_email: testEmail }, { withCredentials: true });
      alert(res.data.message);
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="border border-[#E4E4E7]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2"><Mail className="w-5 h-5" /> Configurazione SMTP</CardTitle>
          <CardDescription className="font-ibm-plex">Parametri per l'invio delle email (buste paga, comunicazioni)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="font-ibm-plex text-xs">Host SMTP</Label>
              <Input value={settings.smtp_host} onChange={(e) => setSettings({...settings, smtp_host: e.target.value})} placeholder="smtp.gmail.com" className="font-ibm-plex" data-testid="smtp-host" />
            </div>
            <div className="space-y-1">
              <Label className="font-ibm-plex text-xs">Porta</Label>
              <Input value={settings.smtp_port} onChange={(e) => setSettings({...settings, smtp_port: e.target.value})} placeholder="587" className="font-ibm-plex" data-testid="smtp-port" />
            </div>
            <div className="space-y-1">
              <Label className="font-ibm-plex text-xs">Sicurezza</Label>
              <Select value={settings.smtp_security || "starttls"} onValueChange={(v) => setSettings({...settings, smtp_security: v})}>
                <SelectTrigger className="font-ibm-plex" data-testid="smtp-security">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starttls">STARTTLS (porta 587)</SelectItem>
                  <SelectItem value="ssl">SSL/TLS (porta 465)</SelectItem>
                  <SelectItem value="none">Nessuna</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="font-ibm-plex text-xs">Utente SMTP</Label>
              <Input value={settings.smtp_user} onChange={(e) => setSettings({...settings, smtp_user: e.target.value})} placeholder="paghe@novahost.it" className="font-ibm-plex" data-testid="smtp-user" />
            </div>
            <div className="space-y-1">
              <Label className="font-ibm-plex text-xs">Password SMTP</Label>
              <Input type="password" value={settings.smtp_password} onChange={(e) => setSettings({...settings, smtp_password: e.target.value})} placeholder="Lascia vuoto per mantenere attuale" className="font-ibm-plex" data-testid="smtp-password" />
            </div>
            <div className="space-y-1">
              <Label className="font-ibm-plex text-xs">Email Mittente</Label>
              <Input value={settings.sender_email} onChange={(e) => setSettings({...settings, sender_email: e.target.value})} className="font-ibm-plex" data-testid="smtp-sender" />
            </div>
            <div className="space-y-1">
              <Label className="font-ibm-plex text-xs">Nome Mittente</Label>
              <Input value={settings.sender_name} onChange={(e) => setSettings({...settings, sender_name: e.target.value})} className="font-ibm-plex" data-testid="smtp-sender-name" />
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-black hover:bg-gray-800 text-white font-ibm-plex" data-testid="save-smtp-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva SMTP"}
          </Button>
          <div className="flex items-end gap-2 pt-2 border-t border-[#E4E4E7]">
            <div className="flex-1 space-y-1">
              <Label className="font-ibm-plex text-xs">Email di test</Label>
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="test@email.com" className="font-ibm-plex" data-testid="smtp-test-email" />
            </div>
            <Button variant="outline" onClick={handleTest} disabled={testing} className="font-ibm-plex" data-testid="smtp-test-btn">
              <Send className="w-4 h-4 mr-2" /> {testing ? "Invio..." : "Invia Test"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-[#E4E4E7]">
        <CardHeader>
          <CardTitle className="font-outfit">Template Busta Paga</CardTitle>
          <CardDescription className="font-ibm-plex">Personalizza oggetto e corpo dell'email. Usa <code className="bg-[#F4F4F5] px-1 rounded">{"{nome}"}</code> e <code className="bg-[#F4F4F5] px-1 rounded">{"{mese}"}</code> come variabili.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label className="font-ibm-plex text-xs">Oggetto Email</Label>
            <Input value={settings.payslip_subject} onChange={(e) => setSettings({...settings, payslip_subject: e.target.value})} className="font-ibm-plex" data-testid="payslip-subject" />
          </div>
          <div className="space-y-1">
            <Label className="font-ibm-plex text-xs">Corpo Email</Label>
            <Textarea value={settings.payslip_body} onChange={(e) => setSettings({...settings, payslip_body: e.target.value})} rows={6} className="font-ibm-plex" data-testid="payslip-body" />
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-black hover:bg-gray-800 text-white font-ibm-plex">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva Template"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { settings, updateSettings, companies, locations, fetchCompanies, fetchLocations } = useApp();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Settings form
  const [formData, setFormData] = useState({
    app_name: "",
    logo_url: "",
    login_image_url: "",
    logo_size: 48,
    google_login_enabled: true,
    primary_color: "#000000",
    api_keys: {}
  });
  const [apiKeyForm, setApiKeyForm] = useState({ key_name: "", key_value: "" });
  
  // Password dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  
  // Logo upload
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLoginImage, setUploadingLoginImage] = useState(false);
  const logoInputRef = useRef(null);
  const loginImageInputRef = useRef(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        app_name: settings.app_name || "Gestionale HR",
        logo_url: settings.logo_url || "",
        login_image_url: settings.login_image_url || "",
        logo_size: settings.logo_size || 48,
        google_login_enabled: settings.google_login_enabled !== false,
        primary_color: settings.primary_color || "#000000",
        api_keys: settings.api_keys || {}
      });
    }
  }, [settings]);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchCompanies();
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, fetchCompanies, fetchLocations]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await updateSettings(formData);
      toast.success("Impostazioni salvate con successo");
    } catch (err) {
      toast.error("Errore nel salvataggio: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddApiKey = () => {
    if (!apiKeyForm.key_name || !apiKeyForm.key_value) return;
    setFormData(prev => ({
      ...prev,
      api_keys: { ...prev.api_keys, [apiKeyForm.key_name]: apiKeyForm.key_value }
    }));
    setApiKeyForm({ key_name: "", key_value: "" });
  };

  const handleRemoveApiKey = (keyName) => {
    setFormData(prev => {
      const newKeys = { ...prev.api_keys };
      delete newKeys[keyName];
      return { ...prev, api_keys: newKeys };
    });
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Il file è troppo grande. Massimo 5MB.");
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await axios.post(`${API_URL}/api/upload/logo`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const newUrl = response.data.url;
      const updated = { ...formData, logo_url: newUrl };
      setFormData(updated);
      await updateSettings(updated);
      toast.success("Logo caricato e salvato");
    } catch (err) {
      toast.error("Errore nel caricamento: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    const updated = { ...formData, logo_url: "" };
    setFormData(updated);
    try {
      await updateSettings(updated);
      toast.success("Logo rimosso");
    } catch (err) {
      toast.error("Errore nella rimozione");
    }
  };

  const handleLoginImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Il file è troppo grande. Massimo 10MB.");
      return;
    }
    
    setUploadingLoginImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const newUrl = response.data.url;
      const updated = { ...formData, login_image_url: newUrl };
      setFormData(updated);
      await updateSettings(updated);
      toast.success("Immagine caricata e salvata");
    } catch (err) {
      toast.error("Errore nel caricamento: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingLoginImage(false);
      if (loginImageInputRef.current) loginImageInputRef.current.value = "";
    }
  };

  const handleRemoveLoginImage = async () => {
    const updated = { ...formData, login_image_url: "" };
    setFormData(updated);
    try {
      await updateSettings(updated);
      toast.success("Immagine rimossa");
    } catch (err) {
      toast.error("Errore nella rimozione");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success("Utente eliminato");
      fetchUsers();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpenPasswordDialog = (userId) => {
    setPasswordUserId(userId);
    setNewPassword("");
    setPasswordDialogOpen(true);
  };

  const handleUpdatePassword = async () => {
    if (!newPassword) {
      toast.error("Inserisci la nuova password");
      return;
    }
    try {
      await axios.put(`${API_URL}/api/users/${passwordUserId}/password`, 
        { password: newPassword }, 
        { withCredentials: true }
      );
      toast.success("Password aggiornata");
      setPasswordDialogOpen(false);
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const countActivePermissions = (perms) => {
    if (!perms) return 0;
    return Object.values(perms).filter(v => v === true).length;
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md border border-[#E4E4E7]">
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-[#52525B] mb-4" />
            <h2 className="text-xl font-outfit font-bold text-[#09090B] mb-2">Accesso Negato</h2>
            <p className="text-[#52525B] font-ibm-plex">
              Solo gli amministratori possono accedere a questa sezione.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B]">
            Amministrazione
          </h1>
          <p className="text-[#52525B] font-ibm-plex mt-1">
            Configura le impostazioni del sistema e gestisci gli utenti
          </p>
        </div>
      </div>

      <Tabs defaultValue="users" className="space-y-6">
        <TabsList className="grid w-full max-w-4xl grid-cols-6">
          <TabsTrigger value="users" className="font-ibm-plex" data-testid="admin-tab-users">
            <Users className="w-4 h-4 mr-2" />
            Utenti
          </TabsTrigger>
          <TabsTrigger value="general" className="font-ibm-plex" data-testid="admin-tab-general">
            <Settings className="w-4 h-4 mr-2" />
            Generale
          </TabsTrigger>
          <TabsTrigger value="branding" className="font-ibm-plex" data-testid="admin-tab-branding">
            <Palette className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="email" className="font-ibm-plex" data-testid="admin-tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Posta
          </TabsTrigger>
          <TabsTrigger value="api" className="font-ibm-plex" data-testid="admin-tab-api">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="changelog" className="font-ibm-plex" data-testid="admin-tab-changelog">
            <Rocket className="w-4 h-4 mr-2" />
            Implementazioni
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="border border-[#E4E4E7]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-outfit">Gestione Utenti</CardTitle>
                <CardDescription className="font-ibm-plex">
                  Crea e gestisci gli utenti del sistema con i relativi permessi
                </CardDescription>
              </div>
              <Button
                data-testid="add-user-button"
                onClick={() => navigate("/admin/users/new")}
                className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Utente
              </Button>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <p className="text-center text-[#52525B] py-8">Caricamento...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F4F4F5]">
                      <TableHead className="font-ibm-plex font-semibold">Nome</TableHead>
                      <TableHead className="font-ibm-plex font-semibold">Email</TableHead>
                      <TableHead className="font-ibm-plex font-semibold">Ruolo</TableHead>
                      <TableHead className="font-ibm-plex font-semibold">Abilitazioni</TableHead>
                      <TableHead className="font-ibm-plex font-semibold">Permessi</TableHead>
                      <TableHead className="font-ibm-plex font-semibold">Stato</TableHead>
                      <TableHead className="font-ibm-plex font-semibold w-[120px]">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-[#F4F4F5]">
                        <TableCell className="font-ibm-plex font-medium">{u.name}</TableCell>
                        <TableCell className="font-ibm-plex text-[#52525B]">{u.email}</TableCell>
                        <TableCell>
                          <Badge className={`${ROLE_LABELS[u.role]?.color || 'bg-gray-500'} text-white font-ibm-plex`}>
                            {ROLE_LABELS[u.role]?.label || u.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-ibm-plex text-[#52525B] text-sm">
                          {u.role === 'admin' ? (
                            <span className="text-green-600">Tutte</span>
                          ) : (
                            <div className="space-y-1">
                              {(u.allowed_companies?.length || 0) > 0 && (
                                <div className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  <span>{u.allowed_companies.length} aziende</span>
                                </div>
                              )}
                              {(u.allowed_locations?.length || 0) > 0 && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  <span>{u.allowed_locations.length} sedi</span>
                                </div>
                              )}
                              {!(u.allowed_companies?.length || u.allowed_locations?.length) && (
                                <span className="text-orange-500">Nessuna</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-ibm-plex text-[#52525B] text-sm">
                          {u.role === 'admin' ? (
                            <span className="text-green-600">Tutti</span>
                          ) : (
                            <span>{countActivePermissions(u.permissions)}/14 attivi</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.is_active !== false ? "default" : "secondary"} className="font-ibm-plex">
                            {u.is_active !== false ? "Attivo" : "Disattivo"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/admin/users/${u.id}`)}
                              data-testid={`edit-user-${u.id}`}
                              title="Modifica"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenPasswordDialog(u.id)}
                              data-testid={`password-user-${u.id}`}
                              title="Cambia password"
                            >
                              <Lock className="w-4 h-4" />
                            </Button>
                            {u.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteUser(u.id)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-user-${u.id}`}
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* General Tab */}
        <TabsContent value="general">
          <Card className="border border-[#E4E4E7]">
            <CardHeader>
              <CardTitle className="font-outfit">Impostazioni Generali</CardTitle>
              <CardDescription className="font-ibm-plex">
                Configura il nome e le informazioni di base del sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="font-ibm-plex">Nome Applicazione</Label>
                <Input
                  data-testid="admin-app-name-input"
                  value={formData.app_name}
                  onChange={(e) => setFormData({ ...formData, app_name: e.target.value })}
                  className="font-ibm-plex max-w-md"
                  placeholder="Es: Gestionale HR"
                />
              </div>

              <Button
                onClick={handleSaveSettings}
                disabled={loading}
                className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Salvataggio..." : "Salva"}
              </Button>

              <div className="pt-4 border-t border-[#E4E4E7]">
                <h3 className="font-ibm-plex font-semibold text-sm mb-2">Manutenzione</h3>
                <Button
                  variant="outline"
                  data-testid="recalculate-schedules-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API_URL}/api/schedules/recalculate-all`, {}, { withCredentials: true });
                      alert(res.data.message || "Ricalcolo completato");
                    } catch (err) {
                      alert("Errore: " + (err.response?.data?.detail || err.message));
                    }
                  }}
                  className="font-ibm-plex text-sm"
                >
                  Ricalcola Statistiche Orari
                </Button>
                <p className="text-xs text-[#71717A] mt-1 font-ibm-plex">Ricalcola ferie, ROL, malattia, 104, lutto, matrimoniale e congedo parentale per tutti gli orari. Da usare dopo aggiornamenti.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding">
          <Card className="border border-[#E4E4E7]">
            <CardHeader>
              <CardTitle className="font-outfit">Personalizzazione</CardTitle>
              <CardDescription className="font-ibm-plex">
                Personalizza l'aspetto dell'applicazione
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label className="font-ibm-plex">Logo Applicazione</Label>
                
                {formData.logo_url ? (
                  <div className="flex items-start gap-4">
                    <div className="relative w-32 h-32 border-2 border-dashed border-[#E4E4E7] rounded-lg overflow-hidden bg-white flex items-center justify-center">
                      <img 
                        src={resolveUrl(formData.logo_url)} 
                        alt="Logo preview" 
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden items-center justify-center text-[#52525B]">
                        <Image className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-[#52525B] font-ibm-plex">Logo attuale</p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="font-ibm-plex"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Cambia
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveLogo}
                          className="font-ibm-plex text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Rimuovi
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => logoInputRef.current?.click()}
                    className="w-full max-w-md h-40 border-2 border-dashed border-[#E4E4E7] rounded-lg flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#09090B] hover:bg-[#F4F4F5] transition-colors"
                  >
                    {uploadingLogo ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-[#09090B] border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-[#52525B] font-ibm-plex">Caricamento...</p>
                      </div>
                    ) : (
                      <>
                        <div className="w-12 h-12 bg-[#F4F4F5] rounded-full flex items-center justify-center">
                          <Upload className="w-6 h-6 text-[#52525B]" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-[#09090B] font-ibm-plex">
                            Clicca per caricare il logo
                          </p>
                          <p className="text-xs text-[#52525B] font-ibm-plex">
                            PNG, JPG, GIF, WEBP o SVG (max 5MB)
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-file-input"
                />
              </div>

              {/* Logo Size Selector */}
              <div className="space-y-3">
                <Label className="font-ibm-plex">Dimensione Logo nel Login</Label>
                <p className="text-xs text-[#52525B] font-ibm-plex">
                  Regola la grandezza del logo mostrato nel form di accesso
                </p>
                <div className="flex items-center gap-4 max-w-md">
                  <Slider
                    data-testid="logo-size-slider"
                    value={[formData.logo_size]}
                    onValueChange={(val) => setFormData({ ...formData, logo_size: val[0] })}
                    min={24}
                    max={300}
                    step={4}
                    className="flex-1"
                  />
                  <span className="text-sm font-ibm-plex-mono text-[#52525B] w-16 text-right" data-testid="logo-size-value">
                    {formData.logo_size}px
                  </span>
                </div>
                {formData.logo_url && (
                  <div className="mt-2 p-3 bg-[#F4F4F5] rounded-lg inline-flex items-center gap-3">
                    <span className="text-xs text-[#52525B] font-ibm-plex">Anteprima:</span>
                    <img 
                      src={formData.logo_url} 
                      alt="Logo size preview" 
                      style={{ height: formData.logo_size }}
                      className="object-contain"
                      data-testid="logo-size-preview"
                    />
                  </div>
                )}
              </div>
              
              {/* Login Image Upload */}
              <div className="space-y-3">
                <Label className="font-ibm-plex">Immagine Sfondo Login</Label>
                <p className="text-xs text-[#52525B] font-ibm-plex">
                  Questa immagine viene mostrata nella pagina di accesso accanto al form
                </p>
                
                {formData.login_image_url ? (
                  <div className="flex items-start gap-4">
                    <div className="relative w-48 h-32 border-2 border-dashed border-[#E4E4E7] rounded-lg overflow-hidden bg-white flex items-center justify-center">
                      <img 
                        src={resolveUrl(formData.login_image_url)} 
                        alt="Login background preview" 
                        className="w-full h-full object-cover"
                        data-testid="login-image-preview"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden items-center justify-center text-[#52525B]">
                        <Image className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-[#52525B] font-ibm-plex">Immagine attuale</p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loginImageInputRef.current?.click()}
                          disabled={uploadingLoginImage}
                          className="font-ibm-plex"
                          data-testid="change-login-image-button"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Cambia
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveLoginImage}
                          className="font-ibm-plex text-red-500 hover:text-red-700"
                          data-testid="remove-login-image-button"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Rimuovi
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => loginImageInputRef.current?.click()}
                    className="w-full max-w-md h-40 border-2 border-dashed border-[#E4E4E7] rounded-lg flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[#09090B] hover:bg-[#F4F4F5] transition-colors"
                    data-testid="login-image-dropzone"
                  >
                    {uploadingLoginImage ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-[#09090B] border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm text-[#52525B] font-ibm-plex">Caricamento...</p>
                      </div>
                    ) : (
                      <>
                        <div className="w-12 h-12 bg-[#F4F4F5] rounded-full flex items-center justify-center">
                          <Image className="w-6 h-6 text-[#52525B]" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-[#09090B] font-ibm-plex">
                            Clicca per caricare l'immagine di sfondo
                          </p>
                          <p className="text-xs text-[#52525B] font-ibm-plex">
                            PNG, JPG, GIF o WEBP (max 10MB)
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                <input
                  ref={loginImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleLoginImageUpload}
                  className="hidden"
                  data-testid="login-image-file-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-ibm-plex">Colore Primario</Label>
                <div className="flex gap-4 items-center">
                  <Input
                    data-testid="admin-color-input"
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="font-ibm-plex-mono w-32"
                  />
                </div>
              </div>
              <Button
                onClick={handleSaveSettings}
                disabled={loading}
                className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Salvataggio..." : "Salva"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email / Posta Tab */}
        <TabsContent value="email">
          <EmailSettingsTab />
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api">
          <Card className="border border-[#E4E4E7]">
            <CardHeader>
              <CardTitle className="font-outfit">Chiavi API</CardTitle>
              <CardDescription className="font-ibm-plex">
                Gestisci le chiavi API per integrazioni esterne
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex gap-4 items-end">
                <div className="flex-1 space-y-2">
                  <Label className="font-ibm-plex">Nome Chiave</Label>
                  <Input
                    data-testid="admin-api-key-name-input"
                    value={apiKeyForm.key_name}
                    onChange={(e) => setApiKeyForm({ ...apiKeyForm, key_name: e.target.value })}
                    className="font-ibm-plex"
                    placeholder="Es: STRIPE_API_KEY"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <Label className="font-ibm-plex">Valore</Label>
                  <Input
                    data-testid="admin-api-key-value-input"
                    type="password"
                    value={apiKeyForm.key_value}
                    onChange={(e) => setApiKeyForm({ ...apiKeyForm, key_value: e.target.value })}
                    className="font-ibm-plex"
                    placeholder="sk_..."
                  />
                </div>
                <Button onClick={handleAddApiKey} variant="outline" className="font-ibm-plex">
                  Aggiungi
                </Button>
              </div>

              {Object.keys(formData.api_keys).length > 0 ? (
                <div className="border rounded-md divide-y divide-[#E4E4E7]">
                  {Object.entries(formData.api_keys).map(([keyName, keyValue]) => (
                    <div key={keyName} className="flex items-center justify-between p-3">
                      <div>
                        <p className="font-ibm-plex font-medium">{keyName}</p>
                        <p className="text-sm text-[#52525B] font-ibm-plex-mono">
                          {keyValue.substring(0, 8)}...{keyValue.substring(keyValue.length - 4)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveApiKey(keyName)}
                        className="text-red-500 hover:text-red-700 font-ibm-plex"
                      >
                        Rimuovi
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#52525B] font-ibm-plex">
                  <Key className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessuna chiave API configurata</p>
                </div>
              )}

              <Button
                onClick={handleSaveSettings}
                disabled={loading}
                className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Salvataggio..." : "Salva"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHANGELOG / IMPLEMENTAZIONI TAB */}
        <TabsContent value="changelog">
          <Card className="border border-[#E4E4E7]">
            <CardHeader>
              <CardTitle className="font-outfit flex items-center gap-2">
                <Rocket className="w-5 h-5" />
                Registro Implementazioni
              </CardTitle>
              <CardDescription className="font-ibm-plex">
                Storico delle funzionalità implementate e aggiornamenti al sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">01/06/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Modulo Compliance e Sicurezza</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Nuovo modulo completo con Dashboard semafori conformità, gestione SCIA Antincendio, squadre minime (Antincendio/Primo Soccorso/HACCP), formazione e certificazioni dipendenti, scadenziario con filtri temporali, documentazione unità locali con 7 categorie e sottocategorie, modulo Emergenze (numeri utili, procedure, registro eventi), reportistica PDF/Excel (Conformità, Scadenziario, Formazione) e alert email.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">28/05/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Portale Dipendenti e Buste Paga</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Area Dipendenti (/dipendente) con login codice fiscale + PIN, timbrature entrata/uscita con orario manuale, sincronizzazione automatica con griglia orari admin, gestione documenti personali e comunicazioni aziendali con anteprima. Upload ZIP buste paga dal commercialista con assegnazione automatica per codice fiscale. Configurazione SMTP in Admin > Posta con template personalizzabile. Import codici fiscali da XLSX. Categorizzazione documenti dipendente.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">23/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Stipendi Nova Service e Dashboard Grafici</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Colonna Paga Lorda per Nova Service, bottone "Genera da Mese Precedente", riepilogo NS con voci editabili (Sicurezza sul lavoro, voce personalizzata), PDF stipendi con riepilogo NS in calce. Dashboard con grafici stipendi: vista globale, per azienda, per unità locale, stipendi vs indennità. Scadenze e Impegni con ricorrenze (unica/mensile/trimestrale/semestrale/annuale).
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">13/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Turno Notturno e Griglia Uniforme</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Calcolo corretto per turni oltre la mezzanotte (es. 22:00-02:00 = 4h), ore attribuite al giorno di partenza. Altezza delle celle uniforme nella griglia orari: tutte le righe si adattano quando presente almeno un turno spezzato. Font ore leggermente ingrandito per migliore leggibilità.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">13/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Etichette Abbreviate e Legenda Completa</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Nelle celle della griglia orari le causali sono abbreviate per evitare il taglio del testo: "Matrim" per Permesso Matrimoniale, "Malatt" per Malattia. Nella legenda in alto alla pagina restano i nomi completi: "Matrimonio" e "Malattia". Caselle non-lavoro interamente colorate con causale centrata.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">10/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Correzione Permessi Utenti</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      I dropdown aziende e sedi ora mostrano solo quelle assegnate all'utente. Le sezioni del menu (Orari, Stipendi, Prestiti, Ghost) sono nascoste se i permessi sono disattivati. Le route sono protette: un utente senza permesso viene reindirizzato alla Dashboard. Prestiti, Contratti e Ghost visibili solo per Admin.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">10/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">PDF Orario con Fasce Orarie</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Il PDF dell'orario mensile ora mostra, per ogni giorno lavorativo, le ore totali e gli orari di ingresso/uscita (es. 8h, 08:00, 16:00) con sfondi colorati per ogni stato (lavoro, riposo, ferie, malattia, ecc.).
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">13/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Trascinamento e Copia/Incolla Orari</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Trascinamento tipo Excel: compilare una cella e trascinare il quadratino nero per riempire i giorni successivi. Tasto destro con menu completo: Modifica, Copia, Incolla, Elimina. Supporto orario spezzato (es. 10:00-14:00 + 19:00-22:00) con calcolo automatico ore totali. Salvataggio in background.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">09/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Personalizzazione Grafica e Orario Compatto</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Orario nelle celle su 2 righe (08:00 / 16:00) per risparmiare spazio. Logo aziendale come icona del browser (favicon). Colore primario impostato da Admin applicato a sidebar, login e header mobile.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">09/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Dipendenti Ghost</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Flag "Ghost" nella scheda dipendente per escluderlo da tutti i PDF ufficiali (commercialista). Nuova pagina dedicata "Ghost" nel menu con orario mensile e stipendi riservati ai soli dipendenti Ghost. Badge GHOST visibile nella lista dipendenti.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">09/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Stampa PDF da sezione Ghost</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Aggiunto bottone "Esporta PDF" sia nel tab Orari che nel tab Stipendi della pagina Ghost. I PDF generati contengono esclusivamente i dipendenti Ghost della sede selezionata.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">09/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Storico Versamenti Prestiti</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Nuova icona occhio sui prestiti per visualizzare lo storico completo: data creazione, riepilogo finanziario e lista di tutti i versamenti con data, importo, commento e badge "SALTATA" per rate a 0€.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">09/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Revisiona Prestito e Pagamento con Data</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Tasto revisiona (matita) per modificare importo totale, rata mensile e note di un prestito. Registrazione pagamenti con data obbligatoria (per inserire anche versamenti pregressi), importo libero (anche 0€ per saltare rata) e commento opzionale.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Processo di Cessazione Dipendente</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Nuovo flusso di cessazione: il dipendente cessato resta nello storico ma non compare nelle liste attive (orari, stipendi). Possibilità di riattivazione. Colonna Stato con badge Attivo/Cessato, filtro "Mostra cessati".
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Export PDF Conteggi Nova Service</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      PDF dedicato per Nova Service con layout professionale: tabella dipendenti (nominativo, giorni lavoro, costo lordo, contributo NS, totale), calcoli totale lordo, 2% sicurezza, totale imponibile, IVA 22%, totale fattura.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Logica Speciale Nova Service</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Per l'azienda Nova Service: colonna "Contributo Nova Service" negli stipendi (si somma al lordo), riepilogo con somma lordo dipendenti, 2% spese gestione costo personale, sicurezza, subtotale + IVA 22%.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Contratti e Scadenze</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Nuova sezione per gestire affitti, mutui, rate, leasing e scadenze con flag di pagamento, alert automatici e card riassuntive.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Orari: Fascia Oraria e Nuovi Stati</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Nella griglia orari ora vengono visualizzati orario inizio/fine oltre alle ore totali. Aggiunti nuovi stati: 104, Permesso Lutto, Permesso Matrimoniale.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Sezione Prestiti</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Nuova pagina Prestiti con gestione completa: inserimento prestito per dipendente, importo totale, rata mensile, registrazione pagamenti, calcolo residuo e sommatoria credito aziendale.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Stipendi: Flag Pagato e Alert</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Aggiunta colonna "Pagato" con checkbox nella tabella stipendi. Alert automatico dal 10 del mese per stipendi non ancora flaggati come pagati.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Stipendi: Colonna Acconti Versati</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Aggiunto campo "Acconti Versati" nel form modifica stipendio e colonna dedicata nella tabella. Gli acconti vengono sottratti dal totale netto.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Stipendio Base Pre-compilato</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Il campo "Paga Netta" nel form di modifica stipendio viene ora pre-compilato automaticamente con lo Stipendio Base impostato nella scheda del dipendente.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">08/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Branding: Slider Dimensione Logo</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Sostituito il selettore dropdown con uno slider scorrevole (24px - 300px) per regolare la dimensione del logo nella pagina di login, con anteprima in tempo reale.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 pb-6 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                    <div className="w-px flex-1 bg-[#E4E4E7] mt-2" />
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">07/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Upload Immagine Sfondo Login</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Possibilità di caricare un'immagine personalizzata come sfondo della pagina di login dal tab Branding. L'immagine viene salvata in modo persistente nel database.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#09090B] flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <div className="pt-1">
                    <p className="text-xs text-[#52525B] font-ibm-plex-mono">07/04/2026</p>
                    <h3 className="font-outfit font-semibold text-[#09090B] mt-1">Persistenza Immagini in Deploy</h3>
                    <p className="text-sm text-[#52525B] font-ibm-plex mt-1">
                      Logo e immagine di login ora vengono salvati in MongoDB (non più su disco locale) per garantire la persistenza anche dopo il deploy. URL relative per compatibilità multi-dominio.
                    </p>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit">Cambia Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-ibm-plex">Nuova Password</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="font-ibm-plex"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)} className="font-ibm-plex">
              Annulla
            </Button>
            <Button 
              data-testid="update-password-button"
              onClick={handleUpdatePassword} 
              className="bg-black hover:bg-gray-800 text-white font-ibm-plex"
            >
              Aggiorna Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
